import { Permissions, usePermissions } from '@app/src/auth/permissions'
import type { ActionButton } from '@app/src/components/ActionButtons'
import CreateNewRequestModal from '@app/src/components/CreateNewRequestModal'
import { usePageBreadcrumbs, useTabCrumb } from '@app/src/components/Navigation/Breadcrumbs/usePageBreadcrumbs'
import CreationModalProgressContextProvider from '@app/src/context/CreationModalProgressContextProvider'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import usePrevious from '@app/src/hooks/previous'
import { useQueryState } from '@app/src/hooks/queryState'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import useShowScreenedRequests from '@app/src/hooks/useShowScreenedRequests'
import AccessorResponseScene from '@app/src/pages/ResourceCollection/Collections/ManageRequests/Response/AccessorResponseScene'
import type { FilterInterface } from '@app/src/pages/ResourceCollection/Filters/Filters'
import { DEFAULT_FILTER_NAME } from '@app/src/pages/ResourceCollection/Filters/Filters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import { insertIf } from '@app/src/utils/helpersTs'
import { mapFiltersBetweenTypes, TypeForFilter } from '@app/src/utils/mapFiltersBetweenTypes'
import paths from '@app/src/wf-constants/paths'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { generatePath, Route, Switch, useLocation, useParams } from 'react-router-dom'
import AccessorRequestsScene from './AccessorRequestsScene'
import OverviewManageRequestsScene from './Overview/OverviewManageRequestsScene'
import ProviderRequestsScene from './ProviderRequestScene'
import AccessorScrapedResponseScene from './Response/AccessorScrapedResponseScene'
import ProviderResponseScene from './Response/ProviderResponseScene'
import ProviderScrapedResponseScene from './Response/ProviderScrapedResponseScene'

interface ManageRequestsSceneParams {
  view: ViewTypeName
}

export enum ViewTypeName {
  Requests = 'requests',
  ScreenedRequests = 'screened-requests',
  Responses = 'responses',
  Overview = 'overview',
}

const toTypeForFilter = (viewTypeName: ViewTypeName) => {
  switch (viewTypeName) {
    case ViewTypeName.Overview:
    case ViewTypeName.Requests:
      return TypeForFilter.Inquiry
    case ViewTypeName.Responses:
      return TypeForFilter.Response
    case ViewTypeName.ScreenedRequests:
      return TypeForFilter.ResponseScraping
  }
}

const mapFilters = (filters: FilterInterface[], previousView: ViewTypeName, view: ViewTypeName) => {
  return mapFiltersBetweenTypes(toTypeForFilter(previousView), toTypeForFilter(view), filters)
}

const ManageRequestsScene: React.FC = () => {
  const { view } = useParams<ManageRequestsSceneParams>()
  const [isCreateDialogOpen, setCreateDialogOpen, closeCreateDialog] = useDialogState()
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const [filters, setFilters] = useQueryState<Array<FilterInterface>>(DEFAULT_FILTER_NAME, [])
  const previousFilters = usePrevious(filters)
  const previousView = usePrevious(view)
  const showScreenedRequests = useShowScreenedRequests()
  const hasWriteAccess = useWriteAccess()
  const isTransparency = hasPermission(Permissions.TRANSPARENCY_USER)
  const location = useLocation<{ openCreateDialog: boolean }>()

  useEffect(() => {
    if (location.state?.openCreateDialog) {
      setCreateDialogOpen()
    }
  }, [])

  useEffect(() => {
    if (!previousView || !previousFilters?.length) return
    const mappedFilters = mapFilters(previousFilters, previousView, view)
    setFilters(mappedFilters)
  }, [view])

  const actionTabs = [
    ...insertIf(!isTransparency, {
      type: ViewTypeName.Overview,
      url: generatePath(paths.manageRequest, { view: ViewTypeName.Overview }),
      label: formatMessage({ id: 'navbar.manageRequestOverview' }),
    }),
    ...insertIf(showScreenedRequests, {
      type: ViewTypeName.ScreenedRequests,
      url: generatePath(paths.manageRequest, { view: ViewTypeName.ScreenedRequests }),
      label: formatMessage({ id: 'navbar.screenedRequests' }),
    }),
    {
      type: ViewTypeName.Requests,
      url: generatePath(paths.manageRequest, { view: ViewTypeName.Requests }),
      label: formatMessage({ id: isTransparency ? 'navbar.receivedRequests' : 'navbar.sentRequests' }),
    },
    {
      type: ViewTypeName.Responses,
      url: generatePath(paths.manageRequest, { view: ViewTypeName.Responses }),
      label: formatMessage({ id: 'navbar.responses' }),
    },
  ]

  const actionButtons: ResourceCollectionSceneProps['actionButtons'] = [
    ...insertIf<ActionButton>(!isTransparency && hasWriteAccess, {
      label: formatMessage({ id: 'resourceCollections.general.createRequest' }),
      onClick: setCreateDialogOpen,
      variant: 'contained',
    }),
  ]

  const manageRequestsCrumb = { label: formatMessage({ id: 'navbar.manageRequests' }) }
  const tabCrumb = useTabCrumb(actionTabs, view)
  usePageBreadcrumbs(tabCrumb ? [manageRequestsCrumb, tabCrumb] : [manageRequestsCrumb])

  return (
    <>
      <Switch>
        {!isTransparency && (
          <Route path={generatePath(paths.manageRequest, { view: ViewTypeName.Overview })}>
            <OverviewManageRequestsScene
              tabs={{ actionTabs, activeTabParam: view }}
              actionButtons={actionButtons}
              setCreateDialogOpen={setCreateDialogOpen}
            />
          </Route>
        )}

        <Route path={generatePath(paths.manageRequest, { view: ViewTypeName.ScreenedRequests })}>
          {isTransparency ? (
            showScreenedRequests ? (
              <ProviderScrapedResponseScene tabs={{ actionTabs, activeTabParam: view }} />
            ) : null
          ) : (
            <AccessorScrapedResponseScene tabs={{ actionTabs, activeTabParam: view }} actionButtons={actionButtons} />
          )}
        </Route>

        <Route path={generatePath(paths.manageRequest, { view: ViewTypeName.Requests })}>
          {isTransparency ? (
            <ProviderRequestsScene tabs={{ actionTabs, activeTabParam: view }} />
          ) : (
            <AccessorRequestsScene
              tabs={{ actionTabs, activeTabParam: view }}
              actionButtons={actionButtons}
              setCreateDialogOpen={setCreateDialogOpen}
            />
          )}
        </Route>

        <Route path={generatePath(paths.manageRequest, { view: ViewTypeName.Responses })}>
          {isTransparency ? (
            <ProviderResponseScene tabs={{ actionTabs, activeTabParam: view }} />
          ) : (
            <AccessorResponseScene tabs={{ actionTabs, activeTabParam: view }} actionButtons={actionButtons} />
          )}
        </Route>
      </Switch>

      <CreationModalProgressContextProvider>
        <CreateNewRequestModal open={isCreateDialogOpen} onClose={closeCreateDialog} />
      </CreationModalProgressContextProvider>
    </>
  )
}

export default ManageRequestsScene
